import { X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from 'src/shared/hooks/authProvider'
import { useQuoteItemApi } from 'src/api/db/apiQuoteItem'
import { ensureQuoteItemCanvasStub } from 'src/api/db/utils/ensureQuoteItemCanvasStub'
import { useSupabaseClient } from 'src/api/supabase'
import { ProductPreview } from 'src/shared/components/ProductGrid/ProductCard/ProductPreview'
import { paths } from 'src/routes/paths'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import type { GalleryDesignRow } from '../../utils/quoteItems'
import { useRequestContext } from '../../hooks/RequestContext'
import { CanvasCard } from './components/CanvasCard'
import { CanvasCommentDraft } from './components/CanvasCommentDraft'
import { CanvasCommentPin } from './components/CanvasCommentPin'
import { CanvasContextMenu } from './components/CanvasContextMenu'
import { CanvasFileCard } from './components/CanvasFileCard'
import { CanvasLinkCard } from './components/CanvasLinkCard'
import { CanvasStampItem } from './components/CanvasStampItem'
import { CanvasStampPicker } from './components/CanvasStampPicker'
import { CanvasSelectionToolbar } from './components/CanvasSelectionToolbar'
import { CanvasToolbar } from './components/CanvasToolbar'
import { CanvasOnboarding } from './components/CanvasOnboarding'
import { getEmptyStateBounds } from './components/CanvasOnboarding/utils'
import {
  DEFAULT_STAMP,
  DOT_GRID_SIZE,
  DRAG_THRESHOLD,
  STROKE_COLOR,
  STROKE_WIDTH,
  STROKE_Z_INDEX,
} from './consts'
import { useCanvasTransform, useQuoteCanvas } from './hooks'
import type { CanvasContextTarget, CanvasTool, CardPosition } from './types'
import {
  cardSelectionId,
  collectMarqueeSelection,
  defaultPositionForIndex,
  fileSelectionId,
  getCardsBounds,
  linkSelectionId,
  normalizeWorldRect,
  parseHttpUrl,
  readClipboardPaste,
  selectionBounds,
  stampSelectionId,
  strokeSelectionId,
  strokeToPath,
  toggleSelectionId,
} from './utils'

type PanState = {
  pointerId: number
  lastClientX: number
  lastClientY: number
  moved: boolean
}

type MarqueeState = {
  pointerId: number
  startWorld: CardPosition
  currentWorld: CardPosition
  additive: boolean
}

export const RequestCanvas = ({
  allDesigns,
  publicId,
  quoteId,
  isReadOnly = false,
}: {
  allDesigns: GalleryDesignRow[] | undefined
  publicId?: string
  quoteId?: number
  isReadOnly?: boolean
}) => {
  const { t, locale } = useLocalization()
  const navigate = useNavigate()
  const { session } = useAuth()
  const isAnonymous = !session?.user?.email
  const supabase = useSupabaseClient()
  const { quote, quoteItems, isDemo } = useRequestContext()
  const { createQuoteItem, deleteQuoteItem } = useQuoteItemApi({
    quote_id: quoteId,
  })
  const containerRef = useRef<HTMLDivElement>(null)
  const panState = useRef<PanState | null>(null)
  const didInitialFit = useRef(false)
  const didInitialOnboardingFit = useRef(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dropDepth = useRef(0)
  const drawPointerId = useRef<number | null>(null)
  const currentStrokeRef = useRef<CardPosition[]>([])
  const pendingDuplicateRef = useRef<
    Array<{
      quoteItemId: number
      position: CardPosition
    }>
  >([])
  const spaceHeldRef = useRef(false)
  const marqueeRef = useRef<MarqueeState | null>(null)
  const groupMoveRef = useRef<{
    primaryId: string
    lastPos: CardPosition
  } | null>(null)

  const [previewItem, setPreviewItem] = useState<GalleryDesignRow | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [marqueeRect, setMarqueeRect] = useState<{
    startWorld: CardPosition
    currentWorld: CardPosition
  } | null>(null)
  const [activeTool, setActiveTool] = useState<CanvasTool>('pan')
  const [draftPosition, setDraftPosition] = useState<CardPosition | null>(null)
  const [openCommentId, setOpenCommentId] = useState<string | null>(null)
  const [currentStroke, setCurrentStroke] = useState<CardPosition[] | null>(
    null
  )
  const [isDropActive, setIsDropActive] = useState(false)
  const [selectedStamp, setSelectedStamp] = useState(DEFAULT_STAMP)
  const [stampCursor, setStampCursor] = useState<CardPosition | null>(null)
  const [contextMenu, setContextMenu] = useState<{
    x: number
    y: number
    target: CanvasContextTarget
  } | null>(null)

  const designs = useMemo(() => allDesigns ?? [], [allDesigns])
  const cardIds = useMemo(
    () => designs.map((d) => d.quoteSubItemId),
    [designs]
  )
  const { transform, panBy, zoomIn, zoomOut, resetZoom, fitToBounds, isPinchingRef } =
    useCanvasTransform(containerRef)
  const {
    positions,
    moveCard,
    bringToFront,
    zIndexOf,
    comments,
    addComment,
    removeComment,
    moveComment,
    files,
    addFiles,
    moveFile,
    removeFile,
    duplicateFile,
    bringFileToFront,
    links,
    addLink,
    moveLink,
    removeLink,
    duplicateLink,
    bringLinkToFront,
    strokes,
    addStroke,
    undoStroke,
    removeStroke,
    stamps,
    addStamp,
    moveStamp,
    removeStamp,
    duplicateStamp,
  } = useQuoteCanvas({
    quoteId,
    publicId,
    initial: quote?.canvas_data,
    isReadOnly,
    cardIds,
    supabase,
    locale,
  })
  const isCanvasEmpty = !designs.length && !files.length && !links.length

  const clearSelection = () => setSelectedIds(new Set())

  const selectItem = (id: string, additive?: boolean) => {
    setSelectedIds((prev) => {
      if (additive) return toggleSelectionId(prev, id)
      if (prev.has(id) && prev.size > 1) return prev
      return new Set([id])
    })
    setOpenCommentId(null)
  }

  const endGroupMove = () => {
    groupMoveRef.current = null
  }

  const moveWithGroup = (
    primaryId: string,
    nextPos: CardPosition,
    currentPrimaryPos: CardPosition,
    applyPrimary: (pos: CardPosition) => void
  ) => {
    const movingGroup =
      selectedIds.has(primaryId) && selectedIds.size > 1

    if (!movingGroup) {
      groupMoveRef.current = null
      applyPrimary(nextPos)
      return
    }

    let session = groupMoveRef.current
    if (!session || session.primaryId !== primaryId) {
      session = { primaryId, lastPos: currentPrimaryPos }
      groupMoveRef.current = session
    }

    const dx = nextPos.x - session.lastPos.x
    const dy = nextPos.y - session.lastPos.y
    session.lastPos = nextPos
    applyPrimary(nextPos)

    selectedIds.forEach((id) => {
      if (id === primaryId) return
      if (id.startsWith('card:')) {
        const subId = Number(id.slice('card:'.length))
        const pos = positions[subId]
        if (!pos) return
        moveCard(subId, { x: pos.x + dx, y: pos.y + dy })
        return
      }
      if (id.startsWith('file:')) {
        const fileId = id.slice('file:'.length)
        const file = files.find((f) => f.id === fileId)
        if (!file) return
        moveFile(fileId, { x: file.x + dx, y: file.y + dy })
        return
      }
      if (id.startsWith('link:')) {
        const linkId = id.slice('link:'.length)
        const link = links.find((l) => l.id === linkId)
        if (!link) return
        moveLink(linkId, { x: link.x + dx, y: link.y + dy })
        return
      }
      if (id.startsWith('stamp:')) {
        const stampId = id.slice('stamp:'.length)
        const stamp = stamps.find((s) => s.id === stampId)
        if (!stamp) return
        moveStamp(stampId, { x: stamp.x + dx, y: stamp.y + dy })
      }
    })
  }

  const switchTool = (tool: CanvasTool) => {
    setActiveTool(tool)
    setDraftPosition(null)
    setOpenCommentId(null)
    setStampCursor(null)
    setContextMenu(null)
  }

  const openContextMenu = (
    e: React.MouseEvent,
    target: CanvasContextTarget
  ) => {
    if (isReadOnly) return
    e.preventDefault()
    e.stopPropagation()
    setContextMenu({ x: e.clientX, y: e.clientY, target })
  }

  const pasteAtPosition = async (position: CardPosition) => {
    const payload = await readClipboardPaste()
    if (!payload) return
    if (payload.type === 'image') {
      await addFiles([payload.file], position)
      return
    }
    await addLink(payload.url, position)
  }

  const duplicateProduct = async (
    quoteItemId: number,
    quoteSubItemId: number
  ) => {
    if (!quoteId) return
    const source = quoteItems?.find((item) => item.id === quoteItemId)
    if (!source?.product_id) return

    try {
      const result = await createQuoteItem.mutateAsync({
        quote_id: quoteId,
        product_id: source.product_id,
        product_variant_color_id: source.product_variant_color_id,
        quantity: source.quantity ?? 1,
        unit_price: source.unit_price,
        type: 'product',
        description: source.description,
      })

      const newQuoteItemId = result.data?.id
      if (!newQuoteItemId) return

      await ensureQuoteItemCanvasStub(supabase, {
        quoteItemId: newQuoteItemId,
        quantity: source.quantity ?? 1,
      })

      const sourcePos =
        positions[quoteSubItemId] ?? defaultPositionForIndex(designs.length)
      pendingDuplicateRef.current.push({
        quoteItemId: newQuoteItemId,
        position: { x: sourcePos.x + 48, y: sourcePos.y + 48 },
      })
    } catch (error) {
      console.error('Failed to duplicate product on canvas', error)
    }
  }

  const deleteContextTarget = (target: CanvasContextTarget) => {
    switch (target.kind) {
      case 'product':
        if (!quoteId) return
        deleteQuoteItem.mutate({
          id: target.quoteItemId,
          quote_id: quoteId,
        })
        setSelectedIds((prev) => {
          const next = new Set(prev)
          next.delete(cardSelectionId(target.quoteSubItemId))
          return next
        })
        break
      case 'file':
        removeFile(target.id)
        setSelectedIds((prev) => {
          const next = new Set(prev)
          next.delete(fileSelectionId(target.id))
          return next
        })
        break
      case 'link':
        removeLink(target.id)
        setSelectedIds((prev) => {
          const next = new Set(prev)
          next.delete(linkSelectionId(target.id))
          return next
        })
        break
      case 'stamp':
        removeStamp(target.id)
        setSelectedIds((prev) => {
          const next = new Set(prev)
          next.delete(stampSelectionId(target.id))
          return next
        })
        break
      case 'comment':
        removeComment(target.id)
        setOpenCommentId(null)
        break
      case 'stroke':
        removeStroke(target.id)
        setSelectedIds((prev) => {
          const next = new Set(prev)
          next.delete(strokeSelectionId(target.id))
          return next
        })
        break
      default:
        break
    }
  }

  const deleteSelectedItems = () => {
    if (isReadOnly || !selectedIds.size) return
    const ids = Array.from(selectedIds)
    ids.forEach((id) => {
      if (id.startsWith('card:')) {
        const subId = Number(id.slice('card:'.length))
        const design = designs.find((d) => d.quoteSubItemId === subId)
        if (!design || !quoteId) return
        deleteQuoteItem.mutate({
          id: design.quoteItemId,
          quote_id: quoteId,
        })
        return
      }
      if (id.startsWith('file:')) {
        removeFile(id.slice('file:'.length))
        return
      }
      if (id.startsWith('link:')) {
        removeLink(id.slice('link:'.length))
        return
      }
      if (id.startsWith('stamp:')) {
        removeStamp(id.slice('stamp:'.length))
        return
      }
      if (id.startsWith('stroke:')) {
        removeStroke(id.slice('stroke:'.length))
      }
    })
    clearSelection()
  }

  const duplicateSelectedItems = async () => {
    if (isReadOnly || !selectedIds.size) return
    const ids = Array.from(selectedIds)
    for (const id of ids) {
      if (id.startsWith('card:')) {
        const subId = Number(id.slice('card:'.length))
        const design = designs.find((d) => d.quoteSubItemId === subId)
        if (!design) continue
        await duplicateProduct(design.quoteItemId, design.quoteSubItemId)
        continue
      }
      if (id.startsWith('file:')) {
        duplicateFile(id.slice('file:'.length))
        continue
      }
      if (id.startsWith('link:')) {
        duplicateLink(id.slice('link:'.length))
        continue
      }
      if (id.startsWith('stamp:')) {
        duplicateStamp(id.slice('stamp:'.length))
      }
    }
  }

  const canDuplicateSelection = Array.from(selectedIds).some(
    (id) =>
      id.startsWith('card:') ||
      id.startsWith('file:') ||
      id.startsWith('link:') ||
      id.startsWith('stamp:')
  )

  // Place duplicated products near the original once the new card appears.
  useEffect(() => {
    const pending = pendingDuplicateRef.current
    if (!pending.length) return
    const remaining: typeof pending = []
    pending.forEach((item) => {
      const newRow = designs.find((d) => d.quoteItemId === item.quoteItemId)
      if (!newRow) {
        remaining.push(item)
        return
      }
      moveCard(newRow.quoteSubItemId, item.position)
    })
    pendingDuplicateRef.current = remaining
  }, [designs, moveCard])

  // Keyboard shortcuts: V select, C comment, P/D draw, F attach, Escape back to select.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) {
        const target = e.target as HTMLElement
        if (
          !target.isContentEditable &&
          !['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
        ) {
          spaceHeldRef.current = true
        }
      }

      if (e.key === 'Escape') {
        if (selectedIds.size) {
          clearSelection()
          return
        }
        if (activeTool === 'pan' && !draftPosition && !openCommentId) return
        setDraftPosition(null)
        setOpenCommentId(null)
        setActiveTool('pan')
        return
      }

      if (isAnonymous) return

      const target = e.target as HTMLElement
      if (
        target.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
      ) {
        return
      }
      if (previewItem) return

      if (e.metaKey || e.ctrlKey) {
        if (
          (e.key === 'v' || e.key === 'V') &&
          (activeTool === 'pan' || activeTool === 'select') &&
          !draftPosition &&
          !openCommentId
        ) {
          e.preventDefault()
          void pasteAtPosition(viewportCenterWorld())
          return
        }
        if (
          (e.key === 'd' || e.key === 'D') &&
          selectedIds.size > 0 &&
          canDuplicateSelection
        ) {
          e.preventDefault()
          void duplicateSelectedItems()
        }
        return
      }

      if (e.altKey) return

      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        selectedIds.size > 0
      ) {
        e.preventDefault()
        deleteSelectedItems()
        return
      }

      switch (e.key.toLowerCase()) {
        case 'h':
          switchTool('pan')
          break
        case 'v':
          switchTool('select')
          break
        case 'c':
          switchTool('comment')
          break
        case 'p':
        case 'd':
          switchTool('draw')
          break
        case 'e':
        case 's':
          switchTool('stamp')
          break
        case 'f':
          fileInputRef.current?.click()
          break
        default:
          return
      }
      e.preventDefault()
    }

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') spaceHeldRef.current = false
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTool,
    draftPosition,
    isAnonymous,
    openCommentId,
    previewItem,
    selectedIds,
  ])

  // Paste images and links onto the canvas.
  useEffect(() => {
    if (isReadOnly || isAnonymous) return

    const onPaste = (e: ClipboardEvent) => {
      if (
        (activeTool !== 'pan' && activeTool !== 'select') ||
        previewItem
      ) {
        return
      }
      const target = e.target as HTMLElement
      if (
        target.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
      ) {
        return
      }

      const items = e.clipboardData?.items
      if (items) {
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            e.preventDefault()
            const file = item.getAsFile()
            if (file) {
              void addFiles([file], viewportCenterWorld())
            }
            return
          }
        }
      }

      const text = e.clipboardData?.getData('text/plain') ?? ''
      const url = parseHttpUrl(text)
      if (url) {
        e.preventDefault()
        void addLink(url, viewportCenterWorld())
      }
    }

    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTool, isAnonymous, isReadOnly, previewItem])

  // Center the view on the empty state when the canvas has no content.
  useEffect(() => {
    if (!isCanvasEmpty || didInitialOnboardingFit.current) return
    didInitialOnboardingFit.current = true
    fitToBounds(getEmptyStateBounds())
  }, [isCanvasEmpty, fitToBounds])

  // Center the view on the cards once everything has a position.
  useEffect(() => {
    if (didInitialFit.current || !cardIds.length) return
    if (cardIds.some((id) => positions[id] == null)) return
    didInitialFit.current = true
    fitToBounds(getCardsBounds(cardIds, positions))
  }, [cardIds, positions, fitToBounds])

  const toWorld = (clientX: number, clientY: number): CardPosition => {
    const rect = containerRef.current?.getBoundingClientRect()
    return {
      x: (clientX - (rect?.left ?? 0) - transform.x) / transform.scale,
      y: (clientY - (rect?.top ?? 0) - transform.y) / transform.scale,
    }
  }

  const viewportCenterWorld = (): CardPosition => {
    const el = containerRef.current
    if (!el) return { x: 0, y: 0 }
    return {
      x: (el.clientWidth / 2 - transform.x) / transform.scale,
      y: (el.clientHeight / 2 - transform.y) / transform.scale,
    }
  }

  const goToEditDecoration = (item: GalleryDesignRow) => {
    if (isReadOnly || !publicId) return
    navigate(
      `${paths.requests}/${publicId}/order_items/${item.quoteItemId}/sub_item/${item.quoteSubItemId}`
    )
  }

  const handleToolChange = (tool: CanvasTool) => {
    switchTool(activeTool === tool ? 'pan' : tool)
  }

  const handleBackgroundPointerDown = (
    e: React.PointerEvent<HTMLDivElement>
  ) => {
    if (e.button !== 0 && e.button !== 1) return
    if (isPinchingRef.current) return
    // Outside-click dismiss can bubble here; don't start a pan/marquee on that gesture.
    if (document.querySelector('[role="dialog"][data-state="open"]')) return
    if ((e.target as HTMLElement).closest('button, a, input, textarea')) return

    const shouldPan =
      isReadOnly ||
      e.button === 1 ||
      spaceHeldRef.current ||
      e.altKey ||
      activeTool !== 'select'

    if (shouldPan) {
      marqueeRef.current = null
      setMarqueeRect(null)
      panState.current = {
        pointerId: e.pointerId,
        lastClientX: e.clientX,
        lastClientY: e.clientY,
        moved: false,
      }
      // Capturing from a child would steal pointerup (tap-to-open preview).
      if (e.target === e.currentTarget) {
        try {
          e.currentTarget.setPointerCapture(e.pointerId)
        } catch {
          // Pointer may already be released; panning still works via bubbling events.
        }
      }
      return
    }

    // Select tool + left drag on empty canvas → marquee
    panState.current = null
    const world = toWorld(e.clientX, e.clientY)
    marqueeRef.current = {
      pointerId: e.pointerId,
      startWorld: world,
      currentWorld: world,
      additive: e.shiftKey || e.metaKey || e.ctrlKey,
    }
    setMarqueeRect({ startWorld: world, currentWorld: world })
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      // Pointer may already be released.
    }
  }

  const handleBackgroundPointerMove = (
    e: React.PointerEvent<HTMLDivElement>
  ) => {
    if (isPinchingRef.current) {
      panState.current = null
      return
    }
    const marquee = marqueeRef.current
    if (marquee && marquee.pointerId === e.pointerId) {
      const world = toWorld(e.clientX, e.clientY)
      marquee.currentWorld = world
      setMarqueeRect({
        startWorld: marquee.startWorld,
        currentWorld: world,
      })
      return
    }

    const pan = panState.current
    if (!pan || pan.pointerId !== e.pointerId) return
    const dx = e.clientX - pan.lastClientX
    const dy = e.clientY - pan.lastClientY
    if (!pan.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD) pan.moved = true
    if (pan.moved) {
      panBy(dx, dy)
      pan.lastClientX = e.clientX
      pan.lastClientY = e.clientY
    }
  }

  const handleBackgroundPointerUp = (
    e: React.PointerEvent<HTMLDivElement>
  ) => {
    if (isPinchingRef.current) {
      panState.current = null
      return
    }
    const marquee = marqueeRef.current
    if (marquee && marquee.pointerId === e.pointerId) {
      marqueeRef.current = null
      setMarqueeRect(null)
      const rect = normalizeWorldRect(marquee.startWorld, marquee.currentWorld)
      const moved =
        Math.hypot(
          marquee.currentWorld.x - marquee.startWorld.x,
          marquee.currentWorld.y - marquee.startWorld.y
        ) > DRAG_THRESHOLD / Math.max(transform.scale, 0.01)

      if (!moved) {
        if (!marquee.additive) clearSelection()
        setOpenCommentId(null)
        return
      }

      const hits = collectMarqueeSelection({
        rect,
        designs,
        positions,
        files,
        links,
        stamps,
        strokes,
      })
      setSelectedIds((prev) => {
        if (marquee.additive) {
          const next = new Set(prev)
          hits.forEach((id) => next.add(id))
          return next
        }
        return new Set(hits)
      })
      setOpenCommentId(null)
      return
    }

    const pan = panState.current
    if (!pan || pan.pointerId !== e.pointerId) return
    panState.current = null
    if (!pan.moved) {
      clearSelection()
      setOpenCommentId(null)
    }
  }

  const handleCommentPlacement = (e: React.MouseEvent<HTMLDivElement>) => {
    setOpenCommentId(null)
    setDraftPosition(toWorld(e.clientX, e.clientY))
  }

  const handleDrawPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    drawPointerId.current = e.pointerId
    currentStrokeRef.current = [toWorld(e.clientX, e.clientY)]
    setCurrentStroke([...currentStrokeRef.current])
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      // Pointer may already be released; drawing still works via bubbling events.
    }
  }

  const handleDrawPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (drawPointerId.current !== e.pointerId) return
    currentStrokeRef.current.push(toWorld(e.clientX, e.clientY))
    setCurrentStroke([...currentStrokeRef.current])
  }

  const handleDrawPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (drawPointerId.current !== e.pointerId) return
    drawPointerId.current = null
    if (currentStrokeRef.current.length > 1) {
      addStroke(currentStrokeRef.current)
    }
    currentStrokeRef.current = []
    setCurrentStroke(null)
  }

  const handleDroppedFiles = (dropped: File[], position: CardPosition) => {
    if (!dropped.length) return
    void addFiles(dropped, position)
  }

  const toContainerPoint = (clientX: number, clientY: number): CardPosition => {
    const rect = containerRef.current?.getBoundingClientRect()
    return { x: clientX - (rect?.left ?? 0), y: clientY - (rect?.top ?? 0) }
  }

  const handleStampPlacement = (e: React.MouseEvent<HTMLDivElement>) => {
    addStamp(toWorld(e.clientX, e.clientY), selectedStamp)
  }

  const selectedStroke = strokes.find((stroke) =>
    selectedIds.has(strokeSelectionId(stroke.id))
  )
  const selectedStrokeMidpoint =
    selectedStroke?.points[Math.floor(selectedStroke.points.length / 2)]

  const deleteSelectedStroke = () => {
    if (!selectedStroke) return
    removeStroke(selectedStroke.id)
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.delete(strokeSelectionId(selectedStroke.id))
      return next
    })
  }

  const activeMarquee = marqueeRect
    ? normalizeWorldRect(marqueeRect.startWorld, marqueeRect.currentWorld)
    : null

  const multiSelectionBounds =
    selectedIds.size > 1
      ? selectionBounds(
          selectedIds,
          designs,
          positions,
          files,
          links,
          stamps,
          strokes
        )
      : null

  const selectionToolbarScreen =
    multiSelectionBounds && containerRef.current
      ? {
          x:
            transform.x +
            ((multiSelectionBounds.minX + multiSelectionBounds.maxX) / 2) *
              transform.scale,
          y: transform.y + multiSelectionBounds.minY * transform.scale,
        }
      : null

  const preview = previewItem?.productPreview

  const worldTransformStyle = {
    transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
    transformOrigin: '0 0',
  } as const

  return (
    <>
      <div
        ref={containerRef}
        className={`relative h-full w-full flex-1 touch-none overscroll-none overflow-hidden ${
          activeTool === 'select'
            ? 'cursor-default'
            : 'cursor-grab active:cursor-grabbing'
        }`}
        style={{
          backgroundImage:
            'radial-gradient(circle, rgb(0 0 0 / 0.14) 1px, transparent 1px)',
          backgroundSize: `${DOT_GRID_SIZE * transform.scale}px ${DOT_GRID_SIZE * transform.scale}px`,
          backgroundPosition: `${transform.x}px ${transform.y}px`,
        }}
        onPointerDownCapture={
          isReadOnly ? handleBackgroundPointerDown : undefined
        }
        onPointerDown={isReadOnly ? undefined : handleBackgroundPointerDown}
        onPointerMove={handleBackgroundPointerMove}
        onPointerUp={handleBackgroundPointerUp}
        onPointerCancel={() => {
          panState.current = null
          marqueeRef.current = null
          setMarqueeRect(null)
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={(e) => {
          e.preventDefault()
          dropDepth.current += 1
          setIsDropActive(true)
        }}
        onDragLeave={() => {
          dropDepth.current -= 1
          if (dropDepth.current <= 0) {
            dropDepth.current = 0
            setIsDropActive(false)
          }
        }}
        onDrop={(e) => {
          e.preventDefault()
          dropDepth.current = 0
          setIsDropActive(false)
          handleDroppedFiles(
            Array.from(e.dataTransfer.files),
            toWorld(e.clientX, e.clientY)
          )
        }}
        onContextMenu={(e) =>
          openContextMenu(e, {
            kind: 'canvas',
            worldPosition: toWorld(e.clientX, e.clientY),
          })
        }
      >
        <div className="absolute left-0 top-0" style={worldTransformStyle}>
          {designs.map((item, index) => {
            const position =
              positions[item.quoteSubItemId] ?? defaultPositionForIndex(index)
            const id = cardSelectionId(item.quoteSubItemId)
            return (
              <CanvasCard
                key={item.quoteSubItemId}
                item={item}
                position={position}
                scale={transform.scale}
                zIndex={zIndexOf(item.quoteSubItemId)}
                selected={selectedIds.has(id)}
                publicId={publicId}
                isReadOnly={isReadOnly || isAnonymous}
                hideMockups={isAnonymous && !isDemo && !isReadOnly}
                onSelect={({ additive } = {}) => {
                  selectItem(id, additive)
                  bringToFront(item.quoteSubItemId)
                }}
                onMove={(pos) =>
                  moveWithGroup(id, pos, position, (next) =>
                    moveCard(item.quoteSubItemId, next)
                  )
                }
                onMoveEnd={endGroupMove}
                onClick={
                  item.productPreview
                    ? () => setPreviewItem(item)
                    : undefined
                }
                onEditClick={
                  isReadOnly ? undefined : () => goToEditDecoration(item)
                }
                onDuplicate={
                  isReadOnly
                    ? undefined
                    : () => {
                        void duplicateProduct(
                          item.quoteItemId,
                          item.quoteSubItemId
                        )
                      }
                }
                onDelete={
                  isReadOnly || !quoteId
                    ? undefined
                    : () => {
                        deleteQuoteItem.mutate({
                          id: item.quoteItemId,
                          quote_id: quoteId,
                        })
                        setSelectedIds((prev) => {
                          const next = new Set(prev)
                          next.delete(id)
                          return next
                        })
                      }
                }
              />
            )
          })}

          {files.map((file) => {
            const id = fileSelectionId(file.id)
            return (
              <CanvasFileCard
                key={file.id}
                file={file}
                scale={transform.scale}
                selected={selectedIds.has(id)}
                onSelect={({ additive } = {}) => {
                  selectItem(id, additive)
                  bringFileToFront(file.id)
                }}
                onMove={
                  isReadOnly
                    ? undefined
                    : (pos) =>
                        moveWithGroup(
                          id,
                          pos,
                          { x: file.x, y: file.y },
                          (next) => moveFile(file.id, next)
                        )
                }
                onMoveEnd={isReadOnly ? undefined : endGroupMove}
                onDelete={
                  isReadOnly
                    ? undefined
                    : () => {
                        removeFile(file.id)
                        setSelectedIds((prev) => {
                          const next = new Set(prev)
                          next.delete(id)
                          return next
                        })
                      }
                }
                onContextMenu={
                  isReadOnly
                    ? undefined
                    : (e) => {
                        if (!selectedIds.has(id)) selectItem(id)
                        openContextMenu(e, { kind: 'file', id: file.id })
                      }
                }
              />
            )
          })}

          {links.map((link) => {
            const id = linkSelectionId(link.id)
            return (
              <CanvasLinkCard
                key={link.id}
                link={link}
                scale={transform.scale}
                selected={selectedIds.has(id)}
                onSelect={({ additive } = {}) => {
                  selectItem(id, additive)
                  bringLinkToFront(link.id)
                }}
                onMove={
                  isReadOnly
                    ? undefined
                    : (pos) =>
                        moveWithGroup(
                          id,
                          pos,
                          { x: link.x, y: link.y },
                          (next) => moveLink(link.id, next)
                        )
                }
                onMoveEnd={isReadOnly ? undefined : endGroupMove}
                onContextMenu={
                  isReadOnly
                    ? undefined
                    : (e) => {
                        if (!selectedIds.has(id)) selectItem(id)
                        openContextMenu(e, { kind: 'link', id: link.id })
                      }
                }
              />
            )
          })}

          {stamps.map((stamp) => {
            const id = stampSelectionId(stamp.id)
            return (
              <CanvasStampItem
                key={stamp.id}
                stamp={stamp}
                scale={transform.scale}
                selected={selectedIds.has(id)}
                onSelect={({ additive } = {}) => selectItem(id, additive)}
                onMove={
                  isReadOnly
                    ? undefined
                    : (pos) =>
                        moveWithGroup(
                          id,
                          pos,
                          { x: stamp.x, y: stamp.y },
                          (next) => moveStamp(stamp.id, next)
                        )
                }
                onMoveEnd={isReadOnly ? undefined : endGroupMove}
                onDelete={
                  isReadOnly
                    ? undefined
                    : () => {
                        removeStamp(stamp.id)
                        setSelectedIds((prev) => {
                          const next = new Set(prev)
                          next.delete(id)
                          return next
                        })
                      }
                }
                onContextMenu={
                  isReadOnly
                    ? undefined
                    : (e) => {
                        if (!selectedIds.has(id)) selectItem(id)
                        openContextMenu(e, { kind: 'stamp', id: stamp.id })
                      }
                }
              />
            )
          })}

          <svg
            className="pointer-events-none absolute left-0 top-0 overflow-visible"
            style={{ zIndex: STROKE_Z_INDEX }}
          >
            {strokes.map((stroke) => {
              const id = strokeSelectionId(stroke.id)
              const isSelected = selectedIds.has(id)
              const path = strokeToPath(stroke.points)
              return (
                <g key={stroke.id}>
                  {isSelected && (
                    <path
                      d={path}
                      fill="none"
                      stroke="#93c5fd"
                      strokeWidth={stroke.width + 8}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      opacity={0.6}
                    />
                  )}
                  <path
                    d={path}
                    fill="none"
                    stroke={stroke.color}
                    strokeWidth={stroke.width}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {/* Invisible wide hit area so the thin line is easy to click. */}
                  <path
                    d={path}
                    fill="none"
                    stroke="transparent"
                    strokeWidth={Math.max(stroke.width + 12, 16)}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="cursor-pointer"
                    style={{ pointerEvents: 'stroke' }}
                    onPointerDown={(e) => {
                      if (e.button !== 0) return
                      e.stopPropagation()
                      selectItem(id, e.shiftKey || e.metaKey || e.ctrlKey)
                    }}
                    onContextMenu={(e) => {
                      e.stopPropagation()
                      if (!selectedIds.has(id)) selectItem(id)
                      openContextMenu(e, { kind: 'stroke', id: stroke.id })
                    }}
                  />
                </g>
              )
            })}
            {currentStroke && currentStroke.length > 1 && (
              <path
                d={strokeToPath(currentStroke)}
                fill="none"
                stroke={STROKE_COLOR}
                strokeWidth={STROKE_WIDTH}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
          </svg>

          {activeMarquee ? (
            <div
              className="pointer-events-none absolute border border-blue-500 bg-blue-500/10"
              style={{
                left: activeMarquee.minX,
                top: activeMarquee.minY,
                width: activeMarquee.maxX - activeMarquee.minX,
                height: activeMarquee.maxY - activeMarquee.minY,
                zIndex: STROKE_Z_INDEX + 1,
              }}
            />
          ) : null}

          {selectedStrokeMidpoint && selectedIds.size === 1 && (
            <button
              type="button"
              className="absolute left-0 top-0 flex h-5 w-5 items-center justify-center rounded-full border border-white bg-gray-900 text-white shadow-md hover:bg-gray-700"
              style={{
                transform: `translate(${selectedStrokeMidpoint.x}px, ${selectedStrokeMidpoint.y}px) translate(-50%, -50%)`,
                zIndex: STROKE_Z_INDEX,
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={deleteSelectedStroke}
              aria-label={t('Remove drawing', 'Ta bort ritning')}
            >
              <X className="h-3 w-3" />
            </button>
          )}

          {isCanvasEmpty && <CanvasOnboarding />}
        </div>

        {selectionToolbarScreen && !isReadOnly ? (
          <CanvasSelectionToolbar
            count={selectedIds.size}
            screenX={selectionToolbarScreen.x}
            screenY={selectionToolbarScreen.y}
            canDuplicate={canDuplicateSelection}
            onDuplicate={() => {
              void duplicateSelectedItems()
            }}
            onDelete={deleteSelectedItems}
          />
        ) : null}

        {activeTool === 'comment' && (
          <div
            className="absolute inset-0 z-30 cursor-crosshair"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={handleCommentPlacement}
          />
        )}

        {activeTool === 'draw' && (
          <div
            className="absolute inset-0 z-30 cursor-crosshair"
            onPointerDown={(e) => {
              e.stopPropagation()
              handleDrawPointerDown(e)
            }}
            onPointerMove={handleDrawPointerMove}
            onPointerUp={handleDrawPointerUp}
            onPointerCancel={handleDrawPointerUp}
          />
        )}

        {activeTool === 'stamp' && (
          <>
            <div
              className="absolute inset-0 z-30 cursor-none"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={handleStampPlacement}
              onPointerMove={(e) =>
                setStampCursor(toContainerPoint(e.clientX, e.clientY))
              }
              onPointerLeave={() => setStampCursor(null)}
            />
            {stampCursor && (
              <span
                className="pointer-events-none absolute left-0 top-0 z-30 p-1 text-4xl leading-none"
                style={{
                  transform: `translate(${stampCursor.x}px, ${stampCursor.y}px) translate(-50%, -50%)`,
                }}
              >
                {selectedStamp}
              </span>
            )}
            <CanvasStampPicker
              selectedStamp={selectedStamp}
              onSelect={setSelectedStamp}
            />
          </>
        )}

        <div
          className="pointer-events-none absolute left-0 top-0 z-30"
          style={worldTransformStyle}
        >
          {comments.map((comment) => (
            <CanvasCommentPin
              key={comment.id}
              comment={comment}
              scale={transform.scale}
              isOpen={openCommentId === comment.id}
              onToggleOpen={() =>
                setOpenCommentId((prev) =>
                  prev === comment.id ? null : comment.id
                )
              }
              onMove={
                isReadOnly
                  ? undefined
                  : (pos) => moveComment(comment.id, pos)
              }
              onDelete={
                isReadOnly
                  ? undefined
                  : () => {
                      removeComment(comment.id)
                      setOpenCommentId(null)
                    }
              }
              onContextMenu={
                isReadOnly
                  ? undefined
                  : (e) =>
                      openContextMenu(e, { kind: 'comment', id: comment.id })
              }
            />
          ))}

          {draftPosition && (
            <CanvasCommentDraft
              position={draftPosition}
              scale={transform.scale}
              onSubmit={(text) => {
                addComment(draftPosition, text)
                setDraftPosition(null)
              }}
              onCancel={() => setDraftPosition(null)}
            />
          )}
        </div>

        {isDropActive && (
          <div className="pointer-events-none absolute inset-3 z-50 flex items-center justify-center rounded-2xl border-2 border-dashed border-blue-400 bg-blue-50/50">
            <p className="rounded-full bg-white px-4 py-2 text-sm text-gray-700 shadow">
              {t('Drop files to add them to the canvas', 'Släpp filer för att lägga till dem på canvasen')}
            </p>
          </div>
        )}

        {(isReadOnly || !isAnonymous) && (
          <CanvasToolbar
            zoomOnly={isReadOnly}
            activeTool={activeTool}
            onToolChange={handleToolChange}
            onAttachFile={() => fileInputRef.current?.click()}
            canUndoStroke={strokes.length > 0}
            onUndoStroke={undoStroke}
            scale={transform.scale}
            onZoomIn={zoomIn}
            onZoomOut={zoomOut}
            onResetZoom={resetZoom}
            onFit={() =>
              fitToBounds(getCardsBounds(cardIds, positions))
            }
          />
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            handleDroppedFiles(
              Array.from(e.target.files ?? []),
              viewportCenterWorld()
            )
            e.target.value = ''
          }}
        />
      </div>

      {contextMenu && (
        <CanvasContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          target={contextMenu.target}
          selectionCount={
            contextMenu.target.kind === 'canvas' ? 1 : Math.max(1, selectedIds.size)
          }
          onClose={() => setContextMenu(null)}
          onPaste={
            contextMenu.target.kind === 'canvas'
              ? (() => {
                  const worldPosition =
                    contextMenu.target.kind === 'canvas'
                      ? contextMenu.target.worldPosition
                      : { x: 0, y: 0 }
                  return () => {
                    void pasteAtPosition(worldPosition)
                  }
                })()
              : undefined
          }
          onDuplicate={(() => {
            if (selectedIds.size > 1 && canDuplicateSelection) {
              return () => {
                void duplicateSelectedItems()
              }
            }
            const target = contextMenu.target
            if (target.kind === 'product') {
              return () => {
                void duplicateProduct(target.quoteItemId, target.quoteSubItemId)
              }
            }
            if (target.kind === 'file') {
              return () => duplicateFile(target.id)
            }
            if (target.kind === 'link') {
              return () => duplicateLink(target.id)
            }
            if (target.kind === 'stamp') {
              return () => duplicateStamp(target.id)
            }
            return undefined
          })()}
          onDelete={
            contextMenu.target.kind !== 'canvas'
              ? () => {
                  if (selectedIds.size > 1) {
                    deleteSelectedItems()
                    return
                  }
                  deleteContextTarget(contextMenu.target)
                }
              : undefined
          }
          onOpenLink={
            contextMenu.target.kind === 'link'
              ? (() => {
                  const linkId =
                    contextMenu.target.kind === 'link'
                      ? contextMenu.target.id
                      : null
                  return () => {
                    const link = linkId
                      ? links.find((l) => l.id === linkId)
                      : undefined
                    if (link) {
                      window.open(link.url, '_blank', 'noopener,noreferrer')
                    }
                  }
                })()
              : undefined
          }
        />
      )}

      {preview && (
        <ProductPreview
          open={previewItem != null}
          setOpen={(open) => {
            if (!open) setPreviewItem(null)
          }}
          product_id={preview.product_id}
          chosen_product_variant_color={preview.chosen_product_variant_color}
          product_variant_colors={preview.product_variant_colors}
          product_name={preview.product_name}
          product_description={preview.product_description}
          moq={preview.moq}
          article_number={preview.article_number}
          price={preview.price}
          klayder_recommends={preview.klayder_recommends}
          klayder_recommendation_text={preview.klayder_recommendation_text}
          product_categories={preview.product_categories}
          addToCurrentQuoteId={isReadOnly ? undefined : quoteId}
        />
      )}
    </>
  )
}
